import { useEffect, useState } from "react";
import SectionCard from "./SectionCard";
import Button from "../../common/Button";
import { useNavigate } from "react-router-dom";
import api from "../../../api/ApiService";
import {
  installmentsList,
  installmentsTypes,
  schemeTypes,
} from "../../../config/appliedSchemesConfig";
import {
  applicationStatusList,
  applicationStatusMap,
} from "../../../config/applicationStatusConfig";
import ConfirmDialog from "../../common/ConfirmDialog";
import { formatDate } from "../../../utils/utils";
import { nodalOfficerDocumentTypeIdMap } from "../../../config/roleConfig";
import { BASE_URL } from "../../../config/apiConfig";

const SubsidyAndBankDetails = ({
  formData,
  setFormData,
  incentiveClaimId,
  applicationId,
  unitName,
  district,
  userId,
  status,
  docsResponse,
  installmentType,
}) => {
  const navigate = useNavigate();
  const [paidMap, setPaidMap] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState("");
  const [selectedAmount, setSelectedAmount] = useState("");
  const [sanctionUploadedFile, setSanctionUploadedFile] = useState(false);
  const [showSactionUpload, setShowSactionUpload] = useState(false);

  const totalMax = parseFloat(formData.total_max_capital_subsidy || 0);
  const totalStampDuty = parseFloat(
    formData.stamp_duty_recommended_investment ||
      formData.total_stamp_duty_per_year ||
      0
  );

  const currentScheme = Object.values(schemeTypes).find(
    (scheme) => scheme.value === String(incentiveClaimId)
  );
  const totalInstallments = currentScheme?.installmentStages || 0;
  const showStampDuty = currentScheme?.showStampDuty || false;

  const [installments, setInstallments] = useState([]);

  useEffect(() => {
    getTransactions();
    const sanctionField = getTransactionReferenceFieldKey();
    setSanctionUploadedFile(formData?.[sanctionField].url);
  }, [applicationId]);

  const getTransactions = async () => {
    try {
      const response = await api.get(`/transactions/${applicationId}`);
      if (response.status === 200) {
        const map = {};
        response.data.forEach((tx) => {
          if (tx.status === "paid") map[String(tx.installment)] = true;
        });
        setPaidMap(map);
      }
    } catch (error) {}
  };

  useEffect(() => {
    let initial = [];

    // 👉 INTEREST ASSISTANCE
    if (String(incentiveClaimId) === schemeTypes.interestassistance.value) {
      const map = {
        1: {
          total: "total_interest_paid_first_installment",
          recommended: "recommended_interest_subsidy_first_installment",
        },
        2: {
          total: "total_interest_paid_second_installment",
          recommended: "recommended_interest_subsidy_second_installment",
        },
        3: {
          total: "total_interest_paid_third_installment",
          recommended: "recommended_interest_subsidy_third_installment",
        },
      };

      initial = Array(totalInstallments)
        .fill(0)
        .map((_, i) => {
          const cfg = map[i + 1];

          // ✅ prefer recommended
          const recommendedValue = formData?.[cfg.recommended];
          if (
            recommendedValue !== undefined &&
            recommendedValue !== null &&
            recommendedValue !== ""
          ) {
            return Number(recommendedValue).toFixed(2);
          }

          // ⬅ fallback to total paid
          const fallbackValue = formData?.[cfg.total] || 0;
          return Number(fallbackValue).toFixed(2);
        });
    }

    // 👉 CAPITAL SUBSIDY
    else {
      const baseAmount = totalMax / totalInstallments || 0;

      initial = Array(totalInstallments)
        .fill(0)
        .map((_, i) => {
          // 2nd installment → check recommended
          if (i + 1 === 2) {
            const recommended =
              formData?.recommended_capital_subsidy_second_installment;

            if (
              recommended !== undefined &&
              recommended !== null &&
              recommended !== ""
            ) {
              return Number(recommended).toFixed(2);
            }
          }

          // ⬅ fallback to existing logic
          const key = `installment${i + 1}`;
          const value =
            formData[key] !== undefined
              ? parseFloat(formData[key])
              : baseAmount;

          return !isNaN(value) ? value.toFixed(2) : "0.00";
        });
    }

    if (JSON.stringify(initial) !== JSON.stringify(installments)) {
      setInstallments(initial);
    }
  }, [totalMax, totalInstallments, formData, incentiveClaimId]);

  useEffect(() => {
    setFormData((prev) => {
      const updated = { ...prev };
      installments.forEach((v, i) => {
        updated[`installment${i + 1}`] = v;
      });
      return updated;
    });
  }, [installments]);

  const handleInstallmentChange = (value, index) => {
    if (!/^\d*\.?\d*$/.test(value)) return;
    const updated = [...installments];
    updated[index] = value;
    setInstallments(updated);
  };

  const handleConfirmAddTransaction = (installment, amount) => {
    setSelectedAmount(amount);
    setSelectedInstallment(installment);
    setShowDialog(true);
  };

  /** MAP installment → Transaction Reference field */
  const getTransactionReferenceFieldKey = () => {
    if (
      String(incentiveClaimId) ===
      schemeTypes.sreimbursementcapitalincentive.value
    ) {
      if (installmentType === "1")
        return "transaction_reference_capital_subsidy_first_installment";
      if (installmentType === "2")
        return "transaction_reference_capital_subsidy_second_installment";
    }

    if (String(incentiveClaimId) === schemeTypes.interestassistance.value) {
      if (installmentType === "1")
        return "transaction_reference_interest_assistance_first_installment";
      if (installmentType === "2")
        return "transaction_reference_interest_assistance_second_installment";
      if (installmentType === "3")
        return "transaction_reference_interest_assistance_third_installment";
    }

    return null;
  };

  /** Upload function */
  const handleTransactionReferenceUpload = async (file, installmentKey) => {
    try {
      const sanctionField = getTransactionReferenceFieldKey(installmentKey);
      if (!sanctionField) return;

      const documentName = nodalOfficerDocumentTypeIdMap[sanctionField];

      const documentTypeId = docsResponse?.find(
        (doc) => doc.name_en === documentName
      )?.id;

      if (!documentTypeId) {
        alert("document_type_id not found for: " + documentName);
        return;
      }

      const existingDocId =
        docsResponse?.find((d) => d?.name_en === documentName)
          ?.upload_documents?.[0]?.id || "";

      const fileFormData = new FormData();
      fileFormData.append("upload_doc", file);
      fileFormData.append("upload_date", formatDate(new Date()));
      fileFormData.append("category_id", 1);
      fileFormData.append("document_type_id", documentTypeId);
      fileFormData.append("application_id", applicationId);
      fileFormData.append("upload_document_id", existingDocId);
      fileFormData.append("user_id", userId);

      const uploadResponse = await api.post(
        "/application/document/upload",
        fileFormData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (uploadResponse?.success && uploadResponse?.data?.path) {
        setFormData((prev) => ({
          ...prev,
          [sanctionField]: uploadResponse.data?.path || "uploaded",
        }));
        setSanctionUploadedFile(uploadResponse.data?.path || "");
      }
    } catch (error) {
      console.error("Upload error:", error);
    }
  };

  const handleAddPayment = async () => {
    setShowDialog(false);
    const payload = {
      applications_id: applicationId,
      user_id: userId,
      category_id: "1",
      amount: selectedAmount,
      status: "paid",
      fullname: unitName,
      payment_mode: "offline",
      district: district,
      subsidy: incentiveClaimId,
      installment: selectedInstallment,
    };
    const response = await api.post("/admin/transactions/add", payload);
    if (response.status === 200) {
      if (
        currentScheme === schemeTypes.sreimbursementcapitalincentive &&
        ((selectedInstallment === installmentsTypes.first.value &&
          !paidMap[installmentsTypes.stampDuty.value] &&
          totalStampDuty > 0) ||
          (selectedInstallment === installmentsTypes.stampDuty.value &&
            !paidMap[installmentsTypes.first.value]))
      ) {
        await getTransactions();
      } else {
        const statusChangeResponse = await api.post(
          "/categories/applications/status/change",
          {
            application_id: applicationId,
            status: applicationStatusMap.paymentDone.value,
            remarks: `Transaction Paid for ${currentScheme.label} for ${
              installmentsList.find((i) => i.value === selectedInstallment).name
            }`,
            application_stage: "",
          }
        );

        if (statusChangeResponse?.status === 200) {
          navigate("/admin/transactions");
        }
      }
    }
  };

  let showButtonList = []; // collect true/false

  useEffect(() => {
    const anyButton = showButtonList.some((v) => v === true);
    setShowSactionUpload(anyButton);
  }, [paidMap, installments, status]);

  const BankDetails = (amount, installment, showButton) => (
    <div className="flex items-center space-x-6 text-sm text-gray-700 ml-4">
      <div className="flex flex-col">
        <span className="font-bold">IFSC</span>
        <span>{formData.subsidy_ifsc_code || "--"}</span>
      </div>
      <div className="flex flex-col">
        <span className="font-bold">Bank</span>
        <span>{formData.subsidy_bank_name || "--"}</span>
      </div>
      <div className="flex flex-col">
        <span className="font-bold">Branch</span>
        <span>{formData.subsidy_branch_name || "--"}</span>
      </div>
      <div className="flex flex-col">
        <span className="font-bold">Acc</span>
        <span>{formData.subsidy_bank_account_number || "--"}</span>
      </div>

      {showButton ? (
        <Button
          className={`px-3 py-2 text-white text-sm rounded ${
            !sanctionUploadedFile ? "bg-gray-400 cursor-not-allowed" : "bg-btn"
          }`}
          disabled={!sanctionUploadedFile}
          onClick={() => handleConfirmAddTransaction(installment, amount)}
        >
          Paid
        </Button>
      ) : (
        <span className="text-green-600 font-semibold">Already Paid</span>
      )}
    </div>
  );

  return (
    <>
      <div className="bg-white rounded p-3 mt-4">
        <SectionCard title="Subsidy Installment">
          {showSactionUpload && (
            <div className="border rounded w-1/2 p-3 text-sm text-gray-700 bg-white shadow-sm mb-4">
              <div className="flex justify-between items-center">
                <span>Transaction Reference File</span>
                <div>
                  <label className="px-3 py-2 mx-3 bg-btn text-white text-sm rounded cursor-pointer hover:opacity-90 transition">
                    Upload Transaction Reference
                    <input
                      type="file"
                      accept={"*"}
                      onChange={(e) => {
                        handleTransactionReferenceUpload(
                          e.target.files[0],
                          installmentType
                        );
                      }}
                      className="hidden"
                    />
                  </label>
                  {sanctionUploadedFile && (
                    <a
                      onClick={() => {
                        window.open(
                          `${BASE_URL}/${sanctionUploadedFile}`,
                          "_blank"
                        );
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-btn text-white text-sm rounded cursor-pointer hover:opacity-90 transition"
                    >
                      View File
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
          {installments.map((amount, index) => {
            const installmentKey = String(index + 1);
            const isPaid = paidMap[installmentKey];
            const isPaymentDone =
              status === applicationStatusMap.paymentDone.value;

            let showButton = false;
            let showStatus = false;

            // --- CAPITAL SUBSIDY LOGIC ---
            if (
              String(incentiveClaimId) ===
              schemeTypes.sreimbursementcapitalincentive.value
            ) {
              if (installmentKey === installmentsTypes.first.value) {
                showButton = !isPaid;
                showStatus = isPaid;
              } else if (installmentKey === installmentsTypes.second.value) {
                const firstPaid = paidMap[installmentsTypes.first.value];
                const stampDutyPaid =
                  paidMap[installmentsTypes.stampDuty.value];

                if (isPaid) {
                  showStatus = true;
                } else if (firstPaid || stampDutyPaid) {
                  showButton = true;
                }
              }
            }

            // --- INTEREST ASSISTANCE LOGIC ---
            else if (
              String(incentiveClaimId) === schemeTypes.interestassistance.value
            ) {
              if (installmentKey === installmentsTypes.first.value) {
                showButton = !isPaid;
                showStatus = isPaid;
              } else {
                const previousPaid = paidMap[String(index)];
                if (isPaid) {
                  showStatus = true;
                } else if (previousPaid) {
                  showButton = true;
                }
              }
            }

            // ✅ Only show “Already Paid” for installments that are actually paid,
            // even if status is "payment done"
            if (isPaymentDone) {
              showButton = false;
              showStatus = !!isPaid; // only if installment itself was paid
            }

            showButtonList.push(showButton);

            const shouldRenderBankDetails = showButton || showStatus;

            return (
              <div key={index} className="flex items-center space-x-3 mb-4">
                <label className="font-medium text-gray-700 w-1/4">
                  {currentScheme.value ===
                  schemeTypes.sreimbursementcapitalincentive.value
                    ? "Capital Subsidy "
                    : currentScheme.value ===
                      schemeTypes.interestassistance.value
                    ? "Interest Assistance "
                    : ""}{" "}
                  Installment {index + 1}
                </label>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) =>
                    handleInstallmentChange(e.target.value, index)
                  }
                  className="border rounded px-2 py-1 w-1/4 bg-gray-100 cursor-not-allowed"
                />
                {shouldRenderBankDetails ? (
                  BankDetails(amount, installmentKey, showButton)
                ) : (
                  <span className="text-gray-600 font-semibold">
                    To be paid in Next Installment
                  </span>
                )}
              </div>
            );
          })}

          {showStampDuty && totalStampDuty > 0 && (
            <div className="flex items-center space-x-3 mb-4">
              <label className="font-medium text-gray-700 w-1/4">
                Stamp Duty Installment
              </label>
              <input
                type="text"
                value={totalStampDuty || ""}
                readOnly
                className="border rounded px-2 py-1 w-1/4 bg-gray-100 cursor-not-allowed"
              />
              {BankDetails(
                totalStampDuty,
                installmentsTypes.stampDuty.value,
                !paidMap[installmentsTypes.stampDuty.value]
              )}
            </div>
          )}
        </SectionCard>
      </div>
      <ConfirmDialog
        isOpen={showDialog}
        title="Add Payment"
        message="Are you sure you want to Pay?"
        onConfirm={handleAddPayment}
        onCancel={() => setShowDialog(false)}
        cancelVisible={true}
      />
    </>
  );
};

export default SubsidyAndBankDetails;
